/**
 * PlotexEnvironmentLoader — Wave 1 Task 2A, Tier 1 (single-file KTX2 path).
 *
 * ---
 * ## What this does
 *
 * Loads an HDR cube environment map from a pre-compiled KTX2 file and returns
 * a **PMREM** (Prefiltered Mipmapped Radiance Environment Map) ready to be
 * assigned to `scene.environment`. That single assignment is what gives
 * three.js PBR materials (MeshStandardMaterial, MeshPhysicalMaterial) real
 * image-based reflections on glass, metal, and any rough surface — replacing
 * our current flat `DirectionalLight` + `AmbientLight` approximation.
 *
 * ## Why PMREM?
 *
 * A raw cube texture gives you *one* reflection sharpness — the source mip.
 * Real PBR wants a different blur per material roughness: a mirror-smooth
 * glass panel samples the sharpest mip, a brushed-metal plate samples a
 * medium-blurred mip, a rough concrete surface samples a heavily-blurred mip.
 *
 * PMREM pre-filters the source cube into a stack of mipmap levels where each
 * level corresponds to a specific GGX roughness bucket. At shader time, three.js
 * picks the right level from the material's roughness value via the
 * "split-sum approximation" — the industry-standard trick that makes real-time
 * IBL tractable. Without PMREM, three.js's PBR shader falls back to a default
 * low-quality environment and reflections look wrong.
 *
 * `PMREMGenerator.fromCubemap(...)` consumes a cube texture and emits the
 * prefiltered version in a single GPU render pass. Calling `.dispose()` after
 * we're done releases the framebuffer/shader resources — the output texture
 * lives on independently and is what we hand back to the caller.
 *
 * ## Why KTX2 BC6H (not .hdr or .exr)?
 *
 * .hdr and .exr are *monolithic* formats — the whole file must download before
 * the renderer can do anything. A Polyhaven 2K HDR is ~6 MB and takes noticeable
 * time on anything slower than fiber. KTX2 with BC6H compression ships the
 * identical dynamic range in roughly **1/10th the bytes** (~500 KB for the same
 * scene) because BC6H is a GPU-native block-compressed HDR format that uploads
 * directly to VRAM with no runtime decode.
 *
 * Plus: the KTX2 container natively ships cube faces + a mip chain, so we hand
 * the decompressed texture straight to `fromCubemap` without any equirect →
 * cube conversion pass. (three.js also ships `fromEquirectangular` for the
 * equirect path, but we don't need it here — the Plotex pipeline emits cube
 * KTX2 via `toktx --cubemap` specifically so the runtime path is trivial.)
 *
 * ## Tier 1 vs Tier 2 — what's in this file today
 *
 * Per user decision #4 in `PLOTEX_MASTER_PLAN.md` section 4.2.1, the initial
 * ship target is the **Tier 1 single-file path**: one KTX2 request, one
 * `PMREMGenerator.fromCubemap(...)` call, done. No mid-load visual updates.
 *
 * **Tier 2** (future follow-up, not in this PR) would progressively upload
 * individual mip levels via `renderer.copyTextureToTexture()` so the first
 * coarse reflection lands within ~50 ms and sharpens over ~200 ms. That
 * optimization only matters on very slow connections; Tier 1 already wins
 * dramatically over a raw .hdr download, so we ship it first and measure
 * whether Tier 2 is worth the engineering complexity on real user traffic.
 *
 * See `PLOTEX_MASTER_PLAN.md` § 4.2.1 for the full two-tier plan.
 */

import type { Texture, WebGLRenderer } from 'three';
import { PMREMGenerator } from 'three';
import type { KTX2Loader } from 'three/examples/jsm/loaders/KTX2Loader.js';

export class PlotexEnvironmentLoader {
  constructor(
    private renderer: WebGLRenderer,
    private ktx2Loader: KTX2Loader,
  ) {}

  /**
   * Load a KTX2 HDR cube texture and return a prefiltered environment map
   * ready to assign to `scene.environment`.
   *
   * The returned texture is the output of `PMREMGenerator.fromCubemap(...)`.
   * Caller is responsible for eventually disposing it via `texture.dispose()`
   * when swapping environments or tearing down the scene. The intermediate
   * PMREMGenerator and source cube texture are cleaned up before this method
   * returns — no leaked render targets.
   *
   * @param url  Path to a cubemap KTX2 (produced by `toktx --cubemap`).
   * @returns    The prefiltered env-map Texture for `scene.environment`.
   */
  async load(url: string): Promise<Texture> {
    // Step 1 — fetch + decode the KTX2 cube texture via three.js's loader.
    // KTX2Loader handles the BasisU/BC6H transcode in a worker pool that was
    // already primed by main.ts (`ktx2Loader.setTranscoderPath('/basis/')` +
    // `detectSupport(renderer)`). The returned object is a CompressedTexture,
    // but when the source KTX2 file carries cube faces it's actually a
    // CompressedCubeTexture (carries `isCubeTexture: true`). We cast because
    // the KTX2Loader.loadAsync declaration only promises CompressedTexture in
    // its .d.ts even though the runtime object matches the CubeTexture shape
    // PMREMGenerator expects. If a non-cube KTX2 is passed here by mistake,
    // `fromCubemap` will surface the error at the next step.
    const cubeTexture = await this.ktx2Loader.loadAsync(url);

    // Step 2 — build a PMREMGenerator bound to our live renderer. The
    // generator allocates a small shader + framebuffer on construction; we
    // dispose both in the finally-block so no GPU resources leak even if
    // `fromCubemap` throws halfway through.
    const pmremGenerator = new PMREMGenerator(this.renderer);

    try {
      // Step 3 — prefilter the source cube into the split-sum radiance map.
      // `fromCubemap` synchronously allocates a WebGLRenderTarget, runs the
      // roughness-aware downsample shader once per mip level, and returns the
      // render target. `.texture` is the actual Texture handle we bind to
      // `scene.environment` — the render target wrapper can be forgotten;
      // three.js holds the underlying framebuffer alive via the texture's
      // parent reference.
      //
      // Type cast: PMREMGenerator.fromCubemap() takes a CubeTexture in its
      // .d.ts but at runtime accepts anything exposing `.isCubeTexture = true`
      // (which CompressedCubeTexture does). This is a known three.js typing
      // gap, not a real compatibility issue.
      const renderTarget = pmremGenerator.fromCubemap(cubeTexture as any);
      const prefilteredTexture = renderTarget.texture;

      // Step 4 — the source cube is no longer needed once the prefiltered
      // output exists. Dispose it so its VRAM slot is returned immediately
      // instead of waiting on the next GC cycle.
      cubeTexture.dispose();

      return prefilteredTexture;
    } finally {
      // Always release the generator — its shaders + pingpong framebuffer
      // aren't needed after `fromCubemap` completes, and leaving them live
      // would pin roughly a megabyte of GPU memory per load call.
      pmremGenerator.dispose();
    }
  }

  /**
   * Release any loader-owned resources.
   *
   * Currently a no-op because the Tier 1 path produces no long-lived state
   * inside the loader instance — each `load()` call is self-contained. This
   * method is reserved for Tier 2 when we'll hold onto the live cube texture
   * across mip uploads and need somewhere to release that cache during scene
   * teardown.
   */
  dispose(): void {
    // Reserved for Tier 2 (progressive mip uploads). Intentionally empty.
  }
}
